import React, { useMemo } from 'react';
import { SurveyTemplate, TemplateCategory } from '../../types/survey.types';
import { extractTemplateVariables } from '../../utils/templateVariables';
import { stripHTML } from '../../utils/stripHTML';

interface TemplateCardProps {
  template: SurveyTemplate;
  onUseTemplate: (template: SurveyTemplate) => void;
  onPreview: (template: SurveyTemplate) => void;
  onEdit: (template: SurveyTemplate) => void;
  onDelete: (template: SurveyTemplate) => void;
  onDuplicate: (template: SurveyTemplate) => void;
  onCategoryChange: (template: SurveyTemplate, category: TemplateCategory) => void;
  canDelete: boolean;
}

const categoryLabels: Record<string, string> = {
  patient_feedback: 'Patient Feedback',
  clinical_research: 'Clinical Research',
  product_evaluation: 'Product Evaluation',
  general: 'General',
  custom: 'Custom',
};

const categoryColors: Record<string, string> = {
  patient_feedback: 'var(--color-primary)',
  clinical_research: 'var(--color-accent)',
  product_evaluation: 'var(--color-success)',
  general: 'var(--color-muted)',
  custom: 'var(--status-warning)',
};

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onUseTemplate,
  onPreview,
  onEdit,
  onDelete,
  onDuplicate,
  onCategoryChange,
  canDelete,
}) => {
  const variableNames = useMemo(() => extractTemplateVariables(template), [template]);

  return (
    <div className="glass-card p-5">
      <div className="flex items-start justify-between gap-4">
        {/* Left: Template info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3
              className="text-base font-semibold truncate"
              style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-heading)' }}
            >
              {template.name}
            </h3>
            {template.isSystemTemplate && (
              <span
                className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0"
                style={{
                  backgroundColor: 'var(--primary-50)',
                  color: 'var(--color-primary)',
                }}
              >
                System
              </span>
            )}
          </div>

          {template.description && (
            <p
              className="text-sm mb-3 line-clamp-2"
              style={{ color: 'var(--color-muted)' }}
            >
              {stripHTML(template.description)}
            </p>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            {/* Category selector */}
            <select
              value={template.category}
              onChange={(e) => onCategoryChange(template, e.target.value as TemplateCategory)}
              className="text-xs px-2 py-0.5 rounded-full font-medium cursor-pointer appearance-none pr-5 border-0 outline-none"
              style={{
                backgroundColor: `${categoryColors[template.category] || 'var(--color-muted)'}15`,
                color: categoryColors[template.category] || 'var(--color-muted)',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%236b7280' d='M1 1l4 4 4-4'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 6px center',
              }}
              title="Change category"
            >
              {Object.entries(categoryLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>

            {/* Stats */}
            <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
              {template.sectionCount} {template.sectionCount === 1 ? 'section' : 'sections'}
            </span>
            <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
              {template.questionCount} {template.questionCount === 1 ? 'question' : 'questions'}
            </span>

            {/* Variable count badge */}
            {variableNames.length > 0 && (
              <span
                className="text-xs px-1.5 py-0.5 rounded font-medium"
                style={{
                  backgroundColor: 'var(--primary-50)',
                  color: 'var(--color-accent)',
                }}
                title={`Variables: ${variableNames.join(', ')}`}
              >
                {variableNames.length} {variableNames.length === 1 ? 'variable' : 'variables'}
              </span>
            )}

            {/* Tags */}
            {template.tags.slice(0, 3).map(tag => (
              <span
                key={tag}
                className="text-xs px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: 'var(--glass-bg-card)',
                  color: 'var(--color-muted)',
                  border: '1px solid var(--glass-border-light)',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Right: Action buttons */}
        <div className="flex flex-col gap-2 flex-shrink-0">
          <button
            onClick={() => onUseTemplate(template)}
            className="glass-btn-primary glass-btn-sm"
            style={{ minWidth: '110px' }}
          >
            Use Template
          </button>
          <button
            onClick={() => onPreview(template)}
            className="glass-btn-secondary glass-btn-sm"
            style={{ minWidth: '110px' }}
          >
            Preview
          </button>
          <button
            onClick={() => onEdit(template)}
            className="glass-btn-secondary glass-btn-sm"
            style={{ minWidth: '110px' }}
          >
            Edit
          </button>
          <button
            onClick={() => onDuplicate(template)}
            className="glass-btn-secondary glass-btn-sm"
            style={{ minWidth: '110px' }}
          >
            Duplicate
          </button>
          {canDelete && (
            <button
              onClick={() => onDelete(template)}
              className="glass-btn-danger-soft glass-btn-sm"
              style={{ minWidth: '110px' }}
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateCard;
